import * as SearchSuggestionClick from '@ori-events/search-suggestion-click';
import { Link, styled, Typography } from '@ori-ui/mui';
import { ContentType, Placement } from '@ori/analytics-data';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useCallback } from 'react';

import { useSearchInput } from '../../context/navigation';
import { useTranslations } from '../../hooks/useTranslations';
import { boldMatchedText } from '../../utils';

const PopularSearchQueriesRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'block',
    marginTop: 0,
  },
}));

const PopularSearchQueriesHeading = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: theme.typography.pxToRem(19),
    margin: theme.spacing(2, 0, 3),
  },
}));

const PopularSearchQueriesResults = styled('ul')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    listStyleType: 'none',
    marginBottom: 0,
    padding: 0,
  },
}));

const PopularSearchQueriesItem = styled('li')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(2),
  },
}));

const PopularSearchQueriesLink = styled(Link)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    color: theme.palette.text.primary,
    textDecoration: 'none',

    '&:hover, &:focus, &:active': {
      color: theme.palette.text.disabled,
    },
  },
}));

const searchQuery = (name: string) => `/search?query=${encodeURIComponent(name)}`;

export const PopularSearchQueries: FC = () => {
  const { results, value } = useSearchInput();
  const { formatMessage } = useTranslations();
  const { getTestId } = useTestId();

  const onClick = useCallback(
    (localBannerTitle: string, position: number) => {
      window.dispatchEvent(
        new CustomEvent<SearchSuggestionClick.Data>(SearchSuggestionClick.Name, {
          detail: {
            itemData: {
              target: searchQuery(localBannerTitle),
              localBannerTitle,
              media: [],
            },
            itemType: ContentType.PopularQuery,
            searchQuery: results?.Query ?? '',
            placement: Placement.TopNavigationSearchSuggestions,
            position,
          },
        }),
      );
    },
    [results?.Query],
  );

  return (
    <PopularSearchQueriesRoot data-testid={getTestId('popular-search-queries-root')}>
      <PopularSearchQueriesHeading data-testid={getTestId('popular-search-queries-heading')}>
        {formatMessage('popularSearchQueries')}
      </PopularSearchQueriesHeading>
      <PopularSearchQueriesResults data-testid={getTestId('popular-search-queries-results')}>
        {results?.Suggestions.map(({ name }, index) => (
          <PopularSearchQueriesItem
            key={name}
            data-testid={getTestId('popular-search-queries-item')}
          >
            <PopularSearchQueriesLink
              href={searchQuery(name)}
              data-testid={getTestId('popular-search-queries-link')}
              onClick={() => onClick(name, index)}
            >
              {boldMatchedText(name, value)}
            </PopularSearchQueriesLink>
          </PopularSearchQueriesItem>
        ))}
      </PopularSearchQueriesResults>
    </PopularSearchQueriesRoot>
  );
};
